import type { CancelablePromise } from "../client"
import { OpenAPI } from "../client"
import { request as __request } from "../client/core/request"

export type CompanyProfileDTO = {
  tenantId: string
  shortName: string
  legalName: string
  wmsName: string
  taxId: string
  regon: string
  krs: string
  bdo: string
  email: string
  phone: string
  website: string
  address: {
    line1: string
    line2: string
    postalCode: string
    city: string
    countryCode: string
  }
  locale: string
  timezone: string
  assets: {
    loginLogo: string
    labelLogo: string
  }
  alt: {
    loginLogo: string
    labelLogo: string
  }
  revisionNumber: number
  labelLogoSource: "default" | "url" | "upload" | "legacy"
}

export type CompanyProfileRevisionDTO = {
  id: string
  revisionNumber: number
  changeReason: string
  createdAt: string
  createdBy: {
    id?: string
    fullName: string
    acronym: string
  }
  snapshot: CompanyProfileDTO
}

export type CompanyProfileLookupFromGUSRequest = {
  nip: string
}

export type CompanyProfileUpdateRequest = {
  shortName: string
  legalName: string
  wmsName: string
  taxId: string
  regon: string
  krs: string
  bdo: string
  email: string
  phone: string
  website: string
  addressLine1: string
  addressLine2: string
  postalCode: string
  city: string
  countryCode: string
  locale: string
  timezone: string
  labelLogo: string
  labelLogoSource: "default" | "url" | "upload" | "legacy"
  labelLogoAlt: string
  changeReason: string
  expectedRevisionNumber: number
}

export type CompanyProfileRevisionConflict = {
  code: "company_profile_revision_conflict"
  error: string
  currentRevisionNumber: number
}

export type CompanyProfileLookupFromGUSResponse = {
  companyName: string
  taxId: string
  regon: string
  krs: string
  bdo: string
  status: string
  startDate: string
  reportName: string
  rawType: string
  address: {
    line1: string
    line2: string
    postalCode: string
    city: string
    countryCode: string
    voivodeship?: string | null
    county?: string | null
    commune?: string | null
  }
  prefill: {
    legalName: string
    taxId: string
    regon: string
    krs: string
    bdo: string
    addressLine1: string
    addressLine2: string
    postalCode: string
    city: string
    countryCode: string
  }
  source: string
  providerMode: string
  mocked: boolean
  mockNote?: string
  requestId?: string
}

export const CompanyProfileService = {
  getPublicProfile: (): CancelablePromise<CompanyProfileDTO> => {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/public/company-profile",
    })
  },

  getProfile: (): CancelablePromise<CompanyProfileDTO> => {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/company-profile/",
    })
  },

  getRevisions: (): CancelablePromise<CompanyProfileRevisionDTO[]> => {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/company-profile/revisions",
    })
  },

  getRevisionById: (
    revisionId: string,
  ): CancelablePromise<CompanyProfileRevisionDTO> => {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/company-profile/revisions/{id}",
      path: {
        id: revisionId,
      },
    })
  },

  updateProfile: (
    requestBody: CompanyProfileUpdateRequest,
  ): CancelablePromise<CompanyProfileDTO> => {
    return __request(OpenAPI, {
      method: "PUT",
      url: "/v1/company-profile/",
      body: requestBody,
      mediaType: "application/json",
    })
  },

  uploadLabelLogo: (
    file: File,
    profile: CompanyProfileUpdateRequest,
  ): CancelablePromise<CompanyProfileDTO> => {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/company-profile/label-logo",
      formData: {
        file,
        profile: JSON.stringify(profile),
      },
      mediaType: "multipart/form-data",
    })
  },

  lookupByGUS: (
    requestBody: CompanyProfileLookupFromGUSRequest,
  ): CancelablePromise<CompanyProfileLookupFromGUSResponse> => {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/company-profile/gus/lookup",
      body: requestBody,
      mediaType: "application/json",
    })
  },
}
